import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGame } from '../contexts/GameContext';
import LoadingSpinner from './LoadingSpinner';
import ErrorMessage from './ErrorMessage';
import Modal from './Modal';
import './Room.css';

const Room = () => {
  const navigate = useNavigate();
  const { state, actions } = useGame();
  const [showLeaveRoomModal, setShowLeaveRoomModal] = useState(false);
  const [showStartGameModal, setShowStartGameModal] = useState(false);

  // 게임 시작 성공 시 게임 페이지로 이동
  useEffect(() => {
    // 게임이 시작되었고, 현재 위치가 게임 페이지가 아닐 때만 이동
    if (state.room.isGameStarted && state.ui.isConnected && window.location.pathname !== '/game') {
      navigate('/game');
    }
  }, [state.room.isGameStarted, state.ui.isConnected, navigate]);
  
  // 게임 종료 후 방으로 돌아왔을 때 상태 업데이트
  useEffect(() => {
    // 게임이 종료되고 방에 있을 때
    if (!state.room.isGameStarted && state.room.id && state.ui.isConnected) {
      // 현재 위치가 게임 페이지라면 방 페이지로 이동
      if (window.location.pathname === '/game') {
        navigate('/room');
      }
    }
  }, [state.room.isGameStarted, state.room.id, state.ui.isConnected, navigate]);

  // 방 나가기 처리
  const handleLeaveRoom = () => {
    actions.leaveRoom();
    navigate('/lobby');
  };

  // 준비 상태 토글
  const toggleReady = () => {
    actions.toggleReady();
  };

  // 게임 시작 처리
  const handleStartGame = () => {
    actions.startGame();
  };

  // 에러 메시지 닫기
  const closeError = () => {
    actions.clearError();
  };

  // 모달 닫기
  const closeModals = () => {
    setShowLeaveRoomModal(false);
    setShowStartGameModal(false);
  };

  // 현재 플레이어가 방장인지 확인
  const isHost = state.room.players?.some(player => player.id === state.player.id && player.isHost) || false;

  // 모든 플레이어가 준비되었는지 확인 (서버에서 전달받은 값 사용)
  const canStartGame = state.room.canStartGame === true;

  const maxSlots = 4;
  const playerSlots = Array.from({ length: maxSlots }, (_, idx) => state.room.players?.[idx] || null);

  return (
    <div className="room-container">
      <div className="room-content">
        <div className="room-top">
          <div className="room-logo">
            <img
              src={`${process.env.PUBLIC_URL}/resource/room/logo.png`}
              alt="방 로고"
            />
          </div>
          <div className="room-info">
            <h2 className="room-title">{state.room.name || '방 이름'}</h2>
            <div className="room-meta">
              <span className="room-id">ID: {state.room.id || '-'}</span>
              <span className="room-count">인원: {state.room.players?.length || 0}/{state.room.maxPlayers || maxSlots}</span>
            </div>
          </div>
        </div>

        <div className="players-section">
          <div className="players-list">
            {playerSlots.map((player, index) => (
              <div
                key={player ? player.id : `empty-${index}`}
                className={`player-slot slot-${index + 1} ${player ? 'has-player' : 'empty'}`}
              >
                <img
                  className="player-illustration"
                  src={`${process.env.PUBLIC_URL}/resource/room/player${index + 1}.png`}
                  alt={`player slot ${index + 1}`}
                />
                <div className={`player-card ${player ? 'has-player' : 'empty'}`}>
                  {player ? (
                    <>
                      <div className="player-info">
                        <span className="player-name">{player.name}</span>
                      </div>
                      <div className="player-status">
                        {player.isHost ? (
                          <span className="host-status">방장</span>
                        ) : player.isReady ? (
                          <span className="ready-status">준비 완료</span>
                        ) : (
                          <span className="not-ready-status">준비 안됨</span>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="player-empty">빈 자리</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="room-actions">
          <button
            className="leave-btn"
            onClick={() => setShowLeaveRoomModal(true)}
          >
            방 나가기
          </button>

          {!isHost && (
            <button
              className={`ready-btn ${state.player.isReady ? 'ready' : 'not-ready'}`}
              onClick={toggleReady}
              disabled={!state.ui.isConnected}
            >
              {state.player.isReady ? '준비 취소' : '준비하기'}
            </button>
          )}
          
          {isHost && (
            <button
              className="start-game-btn"
              onClick={() => setShowStartGameModal(true)}
              disabled={!canStartGame || !state.ui.isConnected}
            >
              게임 시작
            </button>
          )}
        </div>

        {state.ui.error && (
          <ErrorMessage
            message={state.ui.error}
            onClose={closeError}
          />
        )}
      </div>

      {/* 방 나가기 확인 모달 */}
      <Modal
        isOpen={showLeaveRoomModal}
        onClose={closeModals}
        title="방 나가기"
        size="small"
      >
        <p className="leave-room-question">정말로 방에서 나가시겠습니까?</p>
        <div className="modal-actions leave-room-actions">
          <button 
            className="cancel-btn leave-room-cancel-btn modal-image-btn" 
            onClick={closeModals}
            aria-label="취소"
            style={{ width: '240px', height: '60px', minWidth: '120px', backgroundSize: 'contain', backgroundPosition: 'center' }}
          >
            <span className="sr-only">취소</span>
          </button>
          <button 
            className="confirm-btn leave-confirm leave-room-exit-btn modal-image-btn" 
            onClick={handleLeaveRoom}
            disabled={state.ui.isLoading}
            aria-label="나가기"
            style={{ width: '240px', height: '60px', minWidth: '120px', backgroundSize: 'contain', backgroundPosition: 'center' }}
          >
            {state.ui.isLoading ? (
              <LoadingSpinner size="small" message="" />
            ) : (
              <span className="sr-only">나가기</span>
            )}
          </button>
        </div>
      </Modal>

      {/* 게임 시작 확인 모달 */}
      <Modal
        isOpen={showStartGameModal}
        onClose={closeModals}
        title="게임 시작"
        size="small"
      >
        <p className="start-game-text">정말로 게임을 시작하시겠습니까?</p>
        <p className="start-game-text">게임이 시작되면 더 이상 플레이어를 받을 수 없습니다.</p>
        <div className="modal-actions start-game-actions">
          <button 
            className="cancel-btn start-game-cancel-btn modal-image-btn" 
            onClick={closeModals}
            aria-label="취소"
            style={{ width: '240px', height: '60px', minWidth: '120px', backgroundSize: 'contain', backgroundPosition: 'center' }}
          >
            <span className="sr-only">취소</span>
          </button>
          <button 
            className="confirm-btn start-game-start-btn modal-image-btn" 
            onClick={handleStartGame}
            disabled={state.ui.isLoading}
            aria-label="시작"
            style={{ width: '240px', height: '60px', minWidth: '120px', backgroundSize: 'contain', backgroundPosition: 'center' }}
          >
            {state.ui.isLoading ? <LoadingSpinner size="small" /> : <span className="sr-only">시작</span>}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default Room;
